// Sugalarm — wire-level types matching the FastAPI surface.
//
// All glucose values on the wire are mg/dL regardless of `settings.units`
// (units is a display preference). Trend strings are Dexcom-compatible.

export type Trend =
  | "DoubleUp"
  | "SingleUp"
  | "FortyFiveUp"
  | "Flat"
  | "FortyFiveDown"
  | "SingleDown"
  | "DoubleDown";

export type Zone =
  | "urgent_low"
  | "low"
  | "in_range"
  | "high"
  | "urgent_high"
  | "unknown";

export type AlertLevel = "urgent_low" | "low" | "high" | "urgent_high";

export type PredictionLevel =
  | "predicted_urgent_low"
  | "predicted_low"
  | "predicted_high"
  | "predicted_urgent_high";

export type PredictionOutcome = "confirmed" | "false_positive" | "superseded";

export interface User {
  id: string;
  email: string;
  display_name: string;
  cgm_provider: string | null;
  settings: Record<string, unknown> | null;
  created_at: string;
  last_login: string | null;
  /** Cycle 418: opaque per-account key. Absent on a pre-418 server, which
   *  `purgeOnOwnerMismatch` deliberately treats as UNKNOWN (and therefore
   *  purges) rather than as a match. */
  owner_key?: string | null;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  expires_in: number;
}

export interface Reading {
  id: string;
  sgv: number;
  timestamp: string;
  trend: Trend | null;
  trend_rate: number | null;
  provider: string;
  received_at: string;
  /** Cycle 309: server-authoritative zone for this reading ("in_range" |
   *  "low" | "urgent_low" | "high" | "urgent_high"), stamped at
   *  serialization from the member's effective settings via the SAME
   *  mapping the alert engine uses. Optional — absent on history rows and
   *  pre-309 cached payloads; consumers treat missing as "unknown" and
   *  must NOT re-derive it from `sgv` + local thresholds (the TTS
   *  veracity contract). */
  classification?: string | null;
  /** Cycle 309: "synthetic" on dev-injected / demo-feed readings — the
   *  TTS veracity gate never speaks those through the real announcement
   *  path. Absent = real. */
  provenance?: string | null;
}

export interface Alert {
  id: string;
  member_id: string;
  level: AlertLevel;
  sgv: number;
  triggered_at: string;
  fired_at: string | null;
  acknowledged_at: string | null;
  // Cycle 38: cross-device alarm state. Optional so payloads
  // predating the migration deserialize cleanly; treat null/undefined
  // identically.
  snoozed_until?: string | null;
  silenced_at?: string | null;
  // Cycle 63: sustained-alarm heartbeat. Refreshed by the alerts
  // engine on each new reading whose level is unchanged but still
  // alarming, and broadcast over the `alert_heartbeat` WS frame so
  // devices that missed the original edge-trigger frame get a
  // current bindable alert id. Optional / nullable — older payloads
  // and older clients deserialize cleanly.
  last_heartbeat_at?: string | null;
  // Cycle 82: server-authored recovery stamp. Set once by the alerts
  // engine when the episode recovers (the in-range dwell elapsed),
  // then broadcast via the `alert_recovered` frame and carried on the
  // connect-time `latest_alert` snapshot. Terminal — drives the client
  // episode from "live" to recovered/idle. Optional / nullable for
  // wire-compat with rows + clients predating cycle 82.
  recovered_at?: string | null;
  // Cycle 391: the kiosk-side "I'm treating this" window. `custody_until`
  // is the server wall clock the window ends at (null / past = no window),
  // `custody_anchor_sgv` is the LIVE value it was anchored to, and
  // `custody_count` is how many windows this EPISODE has opened (never
  // reset within an episode — it is what forces the third and any later
  // window down to five minutes).
  //
  // All three are SERVER-DERIVED and read-only here. Optional / nullable
  // for wire-compat exactly as `snoozed_until` / `recovered_at` were: a
  // client that booted before the deploy deserializes cleanly and simply
  // never renders the state.
  custody_until?: string | null;
  custody_anchor_sgv?: number | null;
  custody_count?: number | null;
  // Cycle 392: WHO has it. Resolved SERVER-SIDE to a display name, so the
  // client never receives a raw contact id to look up. `null` means the
  // window is anonymous — a normal, fully supported state.
  //
  // The device label, the contact id and any phone number are deliberately
  // NOT on this payload: the label exists to compose one SMS sentence, not
  // to be a field.
  custody_contact_name?: string | null;
  custody_source?: "kiosk" | "app" | null;
  /** Cycle 409: how many heroes "I need help now" would reach from this
   *  window — the server's own recipient rule reduced to one integer.
   *  `null` = unknown / no window; the screen shows the button for it. */
  custody_backup_reach?: number | null;
  /** Cycle 393: WHICH KIND of custody is open.
   *
   *  `"declared"` — somebody SAID they are treating this (a kiosk/SPA tap,
   *  or a press-1 on a call). `"quiet"` — somebody asked for silence and
   *  declared nothing (a snooze). `null` when no custody is open.
   *
   *  The banner reads it to choose between "Treating M:SS" and the
   *  existing "Snoozing M:SS". Both tick off the SERVER `custody_until`
   *  wall clock, so kiosk and phones stay in lockstep. */
  custody_kind?: "declared" | "quiet" | null;
  /** Cycle 456: the open window's start, which is also its identity — the
   *  "Who's treating?" picker posts it back so a stale pick is refused. */
  custody_opened_at?: string | null;
  /** Cycle 456: who took the window, or null (unattributed). */
  treating_by?: TreatingBy | null;
  /** Cycle 456: how long the picker stays up untouched (server-owned). */
  attribution_picker_seconds?: number | null;
  /** Cycle 404: contact UUIDs that pressed 3 ("I can't help right now")
   *  during this episode's escalation calls — "I can't help right now".
   *
   *  EPISODE-SCOPED BY CONSTRUCTION: the list lives on the alert row, so it
   *  dies with the episode. There is no standing "do not call me" here and
   *  none may be inferred from it.
   *
   *  ⚠ IT SUPPRESSES CALLS (and, for a contact linked to an account, that
   *  account's native pushes). It NEVER suppresses texts, and it never
   *  touches the kiosk or any display. Read-only on the client.
   *
   *  Optional / nullable for wire-compat with rows + clients predating the
   *  cycle, exactly as `snoozed_until` / `custody_until` were; every
   *  consumer treats missing and null as the empty list. */
  episode_opt_outs?: string[] | null;
  /** Cycle 407: the check-in. `custody_checkin_question` is the question
   *  posed on the open window (null = none issued yet for this window),
   *  `custody_checkin_expires_at` is the SERVER-written deadline while it
   *  is unanswered (null once answered), `custody_extension_count` is how
   *  many bounded windows answers have bought this episode. All
   *  server-derived; a late-joining device resumes the question from the
   *  row rather than needing to have seen the `custody_checkin` frame. */
  custody_checkin_question?: CustodyCheckinQuestion | null;
  custody_checkin_expires_at?: string | null;
  custody_extension_count?: number | null;
  /** Cycle 450: how many times an answered check-in has RENEWED a window
   *  this episode (the durable server column). Additive; read by nothing
   *  on the client yet — the countdown resets off `custody_until`. */
  custody_checkin_renewals?: number | null;
  /** Cycle 407: WHY a window just ended. Present ONLY on the
   *  `alert_treating` TEARDOWN frame (a transient server attribute, never a
   *  column, never on a REST response) so the hand-back can name its reason
   *  instead of swapping surfaces silently. */
  custody_end_reason?: string | null;
  /** Cycle 407: the live glucose the server acted on when the window ended
   *  — same TEARDOWN frame only, server-stamped, so the hand-back line does
   *  not name a reading the client merely had cached. */
  custody_end_sgv?: number | null;
  /** Cycle 410: the lead window. The SERVER-authored deadline before which
   *  this device holds local alarm audio for the row; `null` = no window,
   *  and the field going null on a later frame is the end signal. Rides
   *  the existing `alert` / `alert_heartbeat` / `alert_treating` frames and
   *  the connect-time `latest_alert` — no new frame type. Optional and
   *  nullable for wire-compat (the cycle-391/392/393 precedent): a client
   *  that booted before the deploy deserialises cleanly and never defers
   *  audio. READ-ONLY on the wire — no request body can set it.
   *
   *  ⚠ NEVER TRUST IT ALONE. The device caps its own timer at
   *  `AUDIO_LEAD_SECONDS` from the frame's receipt (`lib/audibleRow.ts`),
   *  so a bogus far-future value can never wedge the house into silence. */
  audio_lead_until?: string | null;
  /** Cycle 410: WHY a lead window just ended — present ONLY on the teardown
   *  broadcast (a transient server attribute, never a column, never on a
   *  REST response). Informational; the end signal is the null above. */
  audio_lead_end_reason?: string | null;
  /** Cycle 410.1: THE PENDING GRACE — present ONLY on the crossing `alert`
   *  frame (transient, never a column) when a lead window is eligible: a
   *  hard deadline ≤3 s out before which this device holds the URGENT-LOW
   *  audio (a sounding `low` sibling keeps sounding). Resolved by the next
   *  frame for the row — `audio_lead_until` (defer) or nothing (LOUD) — or
   *  by this device's own clock. Capped locally at 3 s from receipt
   *  whatever the value says (`lib/audibleRow.ts`). */
  audio_lead_pending?: string | null;
  /** Cycle 424: the ascent anchor, SERVER-DERIVED and read-only.
   *  `ascent_peak_sgv` is the highest reading since the member climbed out
   *  of the urgent band (null while still inside it); `ascent_broken_at`
   *  is the sticky stamp that the ascent fell back. `lib/ascentLatch.ts`
   *  derives the low-rung latch from them exactly as the server does — an
   *  open, unrecovered, un-silenced urgent-low row with a null stamp.
   *  Optional / nullable for wire-compat; a pre-424 payload simply never
   *  latches on this device. */
  ascent_peak_sgv?: number | null;
  ascent_broken_at?: string | null;
  /** Cycle 427: the prompt mechanism. `custody_prompt_kind` is the ONE live
   *  prompt on the window (`checkin` — "Is everything OK with <member>?";
   *  `worsening` — the same question pulled forward because the member
   *  dropped under a declared window; `still_treating` — "Is <name> still
   *  treating <member>?" when readings return still at or below urgent
   *  low), null when nothing is being asked. `custody_prompt_deadline` is
   *  the SERVER-authored deadline this device counts down from on its own
   *  clock — the dead-man-switch shape of the lead window: a missed frame
   *  never leaves a prompt hanging, and a prompt past its deadline resolves
   *  server-side. Answers carry the kind back; a stale answer to a
   *  superseded prompt is refused. Optional / nullable for wire-compat. */
  custody_prompt_kind?: CustodyPromptKind | null;
  custody_prompt_deadline?: string | null;
  /** Cycle 427: help under an OPEN window. `calling` while the ladder walks
   *  the other heroes beneath the window, `accepted` once one pressed 1;
   *  `custody_help_contact_name` is the hero being rung or the one who
   *  accepted, resolved SERVER-SIDE to a display name. Both transient,
   *  both server-derived; null when no help was asked for. */
  custody_help_state?: "calling" | "accepted" | null;
  custody_help_contact_name?: string | null;
  /** Cycle 427: the kiosk tone is quiet while an urgent-low row is open
   *  with NO window and a STALE feed (operator-authorized 2026-09-08).
   *  SERVER-authored; consumed ONLY by the audible-row selection
   *  (`lib/audibleRow.ts`), which honours it only with no window and a
   *  reading this device can see is stale on its own clock — a bogus true
   *  can never quiet a live feed. Never read by any gate or control. */
  no_data_quiet?: boolean | null;
  reading_id: string | null;
  created_at: string;
}

/** Cycle 427: the three prompt kinds one mechanism serves. `checkin` is the
 *  8-minute check-in (and the same question at expiry on a dark feed);
 *  `worsening` is the check-in pulled forward under a declared window;
 *  `still_treating` asks whether the declared caregiver is still there
 *  when readings return still at or below urgent low. (Cycle 427 retired
 *  the cycle-407 `status` / `more_time` / `offer_backup` questions: there
 *  is one question, "no" is the help path, and there is no extension cap.) */
export type CustodyPromptKind = "checkin" | "worsening" | "still_treating";
/** The `custody_checkin` frame's `question` and the sheet's kind. Kept
 *  under its cycle-407 name for the readers; the VALUES are the prompt
 *  kinds. */
export type CustodyCheckinQuestion = CustodyPromptKind;

/** Cycle 407: the check-in answer surface — `yes` / `no` answer the pending
 *  question; `help` ("I need help now") and `resume` ("Resume alerts") are
 *  the treating screen's standing actions and need no pending question. */
export type CustodyCheckinAnswer = "yes" | "no" | "help" | "resume";

/** Cycle 407 → 427: what the server DID with an answer. `extended` (a
 *  check-in "yes": ten more minutes, No Data mode included); `reset` (a
 *  worsening / still-treating "yes": re-anchored, ten fresh minutes);
 *  `declined` (a still-treating "no": the window ended and a fresh search
 *  started, calls first); `backup` / `backup_repeat` / `backup_unsent`
 *  (the help path — the window STAYS OPEN and the other heroes are rung
 *  beneath it; only `backup` means a text actually left); `resumed`;
 *  `resolved` (nothing was pending). Cycle 450: EVERY check-in "yes" is
 *  `extended` (the window renewed to its full ten minutes) — `winding_down`
 *  is retired server-side; `capped` is UN-retired for the operator lever
 *  `SUGALARM_CUSTODY_CHECKIN_RENEWALS_MAX` (the answer was recorded, the
 *  window was not renewed; the line says when alerts resume). `no_reading`,
 *  `ask_more_time` and `winding_down` may still arrive from an older server
 *  and must render harmlessly. */
export type CustodyCheckinOutcome =
  | "extended"
  | "winding_down"
  | "reset"
  | "declined"
  | "backup"
  | "backup_repeat"
  | "backup_unsent"
  | "resumed"
  | "resolved"
  | "no_reading"
  | "ask_more_time"
  | "capped";

export interface CustodyCheckinResult {
  outcome: CustodyCheckinOutcome;
  alert: Alert;
}

export interface Prediction {
  id: string;
  member_id: string;
  reading_id: string | null;
  predicted_level: PredictionLevel;
  predicted_at: string;
  horizon_minutes: number;
  predicted_sgv: number;
  slope_mgdl_per_min: number;
  slope_r_squared: number | null;
  fired_at: string | null;
  acknowledged_at: string | null;
  outcome: PredictionOutcome | null;
  outcome_evaluated_at: string | null;
  outcome_reading_id: string | null;
  created_at: string;
}

// Cycle 84: web + kiosk theme. `midnight` is the pre-cycle dark navy
// look (the default); the other 5 are cool, calming dark grounds.
// Mobile uses `display_dark_mode` (Light/Dark) and ignores this.
export type DisplayTheme =
  | "midnight"
  | "deep-sea"
  | "evergreen"
  | "twilight"
  | "slate"
  | "lagoon";

// Cycle 171: mobile-only selectable app background. "aurora" (the
// cycle-164 default) plus 17 built-in presets, grouped Calm / Scenic /
// Adventure in the picker. Web + kiosk ignore this key.
export type MobileBackground =
  | "aurora"
  | "soft-mesh"
  | "dawn-wash"
  | "contour"
  | "dot-grid"
  | "single-orb"
  | "aurora-cool"
  | "sea-glass"
  | "morning-fog"
  | "warm-linen"
  | "lavender-mist"
  | "sage-calm"
  | "golden-plateau"
  | "dawn-mist"
  | "verdant-calm"
  | "heros-horizon"
  | "sacred-glade"
  | "twilight-keep"
  // Cycle 174: premium preset (pack "legends") — selectable only when entitled.
  | "celestial"
  // Cycle 5: premium image pack "relics" (hosted WebP) — selectable only when
  // entitled; renders the built-in aurora until the art is uploaded.
  | "relic-sword"
  | "relic-shield"
  | "relic-amulet"
  // Cycle 189: admins can now mint NEW image-preset ids at runtime (uploaded
  // background art), so the value is no longer a closed set. `(string & {})`
  // keeps autocomplete for the built-in literals above while accepting any
  // server-minted preset id. Server-validated against the merged catalog.
  | (string & {});

// Cycle 84: an uploaded theme background image's metadata (the bytes are
// fetched separately, with auth, from `image_url`).
export interface Background {
  id: string;
  // Cycle 90: the member this background is scoped to (forward-compatible;
  // every account that can see the member fetches/renders it).
  member_id: string;
  content_type: string;
  byte_size: number;
  // Cycle 87: pixel dimensions of the stored (cropped) image; null for
  // rows uploaded before the cropper shipped.
  width: number | null;
  height: number | null;
  created_at: string;
  original_filename: string | null;
  image_url: string;
}

export interface UserSettings {
  units: "mg/dL" | "mmol/L";
  target_low: number;
  target_high: number;
  urgent_low: number;
  urgent_high: number;
  alerts_enabled: boolean;
  // Cycle 228: repeating critical-alarm escalation. When enabled the
  // server re-sends the critical push every N seconds while an alarm is
  // live + unacknowledged (Dexcom-style persistence on a locked phone).
  // Cycle 422: 120–1800 s (see `lib/alarmRepeat.ts`); the toggle governs
  // a plain low only — an urgent low always repeats.
  alarm_repeat_enabled: boolean;
  alarm_repeat_interval_seconds: number;
  display_24h: boolean;
  display_dark_mode: boolean;
  // Cycle 118: IOB dual-ring color overrides. null = the stock token
  // defaults (#355e3b meal / #ffd60a correction); a set value is a
  // normalized lowercase #rrggbb hex. `iob_meal_color` drives
  // --iob-ring-first, `iob_correction_color` drives --iob-ring-second.
  iob_meal_color: string | null;
  iob_correction_color: string | null;
  // Cycle 147: the long-acting (basal) outer ring → --iob-ring-third.
  iob_basal_color: string | null;
  // Cycle 303: server-coordinated IOB-card dismissal (UTC ISO-8601 or
  // null). Fanned out via settings_update; the card hides on every device
  // when all current context is older AND no basal is due.
  iob_card_dismissed_at: string | null;
  // Cycle 84: web + kiosk theme + selected uploaded background.
  display_theme: DisplayTheme;
  display_background_id: string | null;
  // Cycle 171: mobile-only app background (default "aurora").
  display_mobile_background: MobileBackground;
  // Cycle 77: kiosk navigation position (bottom pill ↔ left rail).
  nav_position: "bottom" | "side";
  prediction_enabled: boolean;
  prediction_horizon_minutes: number;
  prediction_min_slope_abs: number;
  prediction_min_r_squared: number;
  // Cycle 16; cycle 330: an opaque voice_id, no longer a locale short-name.
  tts_voice: string;
  tts_announce_interval_minutes: number;
  // Cycle 330: voice quiet hours. Pauses ONLY the auto-announce cadence and
  // the spoken half of a fired reminder (the toast + push still arrive).
  // Local wall-clock HH:MM; `end <= start` wraps past midnight, same as
  // `alarm_clock`. Never suppresses an alarm, alert, or the manual Speak
  // Status / Speak sample buttons. See `lib/quietHours.ts`.
  tts_quiet_hours_enabled: boolean;
  tts_quiet_hours_start: string;
  tts_quiet_hours_end: string;
  // Cycle 145: alarm-clock-mode nightly schedule. days[0] = Sunday; an
  // overnight window (end <= start) wraps past midnight.
  alarm_clock: AlarmClockSchedule;
  // Cycle 242: per-zone custom in-app alarm/recovery sound. "default" = the
  // original hardcoded file (and the mandatory fallback); else a curated
  // skin id from `lib/alarmSoundOptions.ts`.
  alarm_sound_urgent_low: string;
  alarm_sound_low: string;
  alarm_sound_inrange: string;
  alarm_sound_high: string;
  alarm_sound_urgent_high: string;
  // Cycle 354: sensor-pause hold — the household declared the feed
  // expectedly dark (changing sensor / away), standing the cycle-353
  // blackout pages down until `until`. Read-only on the settings surface;
  // written via POST/DELETE /api/members/{id}/sensor_pause. Gates ONLY
  // the blackout push rail — never a glucose alarm.
  sensor_pause: SensorPause | null;
}

// Cycle 354 — see UserSettings.sensor_pause.
export interface SensorPause {
  reason: "sensor_change" | "away";
  until: string; // ISO datetime
  set_by: string | null;
}

// Cycle 145 — the alarm-clock-mode schedule sub-object.
export interface AlarmClockSchedule {
  enabled: boolean;
  /** "HH:MM" 24h. */
  start: string;
  /** "HH:MM" 24h; end <= start = overnight wrap. */
  end: string;
  /** Exactly 7 booleans, index 0 = Sunday. */
  days: boolean[];
  /** Cycle 166: show the IOB rings on the bedside alarm-clock face.
   *  Optional so a cached pre-166 blob still type-checks; consumers
   *  read it `?? true` (rings shown — today's behavior). Governs the
   *  alarm-clock face ONLY — no other ring surface reads it. */
  show_iob_rings?: boolean;
}

export const DEFAULT_THRESHOLDS = {
  urgent_low: 55,
  target_low: 70,
  target_high: 180,
  urgent_high: 250,
};

export type Thresholds = typeof DEFAULT_THRESHOLDS;

export interface Treatment {
  id: string;
  member_id: string;
  user_id: string;
  event_type: "carbs" | "insulin" | "bg_check" | "note" | "exercise";
  timestamp: string;
  carbs_g: number | null;
  insulin_u: number | null;
  notes: string | null;
  entered_by: string | null;
  // Cycle 32: structured row-origin metadata. At minimum
  // `{ source: "manual" | "photo" }`; source-specific keys (model,
  // request_id, confidence for `photo`) pass through unvalidated.
  provenance: Record<string, unknown>;
  // Cycle 121: long-acting (basal) insulin. `insulin_kind` is always
  // serialized ('bolus' for every existing/non-insulin row); the IOB
  // engine excludes 'basal' rows from the bolus rings and renders them as
  // a separate depleting fuel line. `insulin_duration_min` is the
  // per-dose minutes of action (1440 for a 24h Lantus; null for a bolus);
  // `insulin_label` is the optional product name. Optional in the type so
  // cached rows / frames predating the migration deserialize cleanly —
  // treat a missing `insulin_kind` as 'bolus'.
  insulin_kind?: "bolus" | "basal";
  insulin_duration_min?: number | null;
  insulin_label?: string | null;
  // Cycle 126: the user's explicit bolus intent from the guided Insulin
  // flow. Authoritative over the inference when set; null/undefined on
  // every pre-126 / non-guided row → the Cycle 125 inference path.
  dose_intent?: "meal" | "correction" | null;
  created_at: string;
}

export interface Device {
  id: string;
  user_id: string;
  name: string;
  device_type: "kiosk" | "web" | "mobile" | "other";
  client_id: string;
  last_seen_at: string | null;
  last_ip: string | null;
  user_agent: string | null;
  has_token: boolean;
  token_issued_at: string | null;
  token_revoked_at: string | null;
  // Cycle 100 — per-device wake word (selection key, e.g. "hey-buzz").
  // null = unset; the kiosk agent uses its local default.
  wake_word: string | null;
  created_at: string;
  // Cycle 175 — derived: true for rows prune would KEEP (live unrevoked
  // token, or seen recently), false for rows it would delete. Drives the
  // Settings "Show inactive" filter. Optional for forward-compat with
  // older server responses (defaults to active so nothing hides wrongly).
  active?: boolean;
  // Cycle 200 — camera capability self-reported by the device (the
  // Android tablet kiosk reports true). Optional for forward-compat.
  camera_capable?: boolean;
}

export interface TreatmentCreateIn {
  event_type: "carbs" | "insulin" | "bg_check" | "note" | "exercise";
  timestamp: string; // ISO-8601 UTC
  carbs_g?: number | null;
  insulin_u?: number | null;
  notes?: string | null;
  entered_by?: string | null;
  // Cycle 32: optional structured row-origin. Server requires
  // `source` to be one of the known strings when provided; the rest
  // of the object is metadata.
  provenance?: Record<string, unknown>;
  // Cycle 121: long-acting (basal) entry. Omit for a normal bolus (the
  // submit is byte-identical to today). When `insulin_kind='basal'` the
  // server requires event_type='insulin' + insulin_u > 0 and defaults
  // insulin_duration_min to 1440 when omitted.
  insulin_kind?: "bolus" | "basal";
  insulin_duration_min?: number | null;
  insulin_label?: string | null;
  // Cycle 126: explicit bolus intent ('meal' | 'correction'). Only valid
  // on a bolus insulin row; the server rejects it elsewhere (422).
  dose_intent?: "meal" | "correction" | null;
}

// Cycle 34.20: PATCH /api/treatments/{id} partial payload. Every
// field is optional; unset fields stay untouched on the server.
export interface TreatmentUpdateIn {
  event_type?: "carbs" | "insulin" | "bg_check" | "note" | "exercise";
  timestamp?: string;
  carbs_g?: number | null;
  insulin_u?: number | null;
  notes?: string | null;
  entered_by?: string | null;
  provenance?: Record<string, unknown>;
  // Cycle 121: carried so an edit PATCH preserves the basal fields.
  insulin_kind?: "bolus" | "basal";
  insulin_duration_min?: number | null;
  insulin_label?: string | null;
  // Cycle 126: carried so an edit PATCH preserves the dose intent.
  dose_intent?: "meal" | "correction" | null;
}

// ─── Reminders (cycle 76) ───────────────────────────────────────────────

export type WeekdayCode =
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat"
  | "sun";

export interface Reminder {
  id: string;
  user_id: string;
  member_id: string | null;
  /** Resolved server-side so the label/spoken text survives an archived
   *  member. */
  member_name: string | null;
  message: string;
  /** "HH:MM" 24-hour wall-clock, interpreted in `timezone`. */
  time_of_day: string;
  /** IANA zone, e.g. "America/Chicago". */
  timezone: string;
  /** Recurring weekdays (empty when one-off). */
  weekdays: WeekdayCode[];
  /** "YYYY-MM-DD" single occurrence, or null when recurring. */
  one_off_date: string | null;
  enabled: boolean;
  last_fired_at: string | null;
  created_at: string;
}

/** Cycle 362: one durable fired reminder OCCURRENCE, server-authoritative.
 *  Carries its own ack (`dismissed_at`) so a Close survives a reload, a
 *  reboot, and a device that was offline when the broadcast went out.
 *  Render `time_of_day` / `scheduled_for` — NEVER a local `new Date()` — so a
 *  reminder surfaced after a delay is labelled with the time it was FOR. */
export interface ReminderFire {
  id: string;
  reminder_id: string;
  /** ISO-8601 UTC instant of the occurrence (10:00 local, in `timezone`). */
  scheduled_for: string;
  fired_at: string;
  dismissed_at: string | null;
  message: string;
  member_name: string | null;
  /** IANA zone the `time_of_day` wall clock belongs to. */
  timezone: string;
  /** "HH:MM" 24-hour wall clock this occurrence was scheduled for. */
  time_of_day: string;
}

export interface ReminderCreateIn {
  message: string;
  time_of_day: string; // "HH:MM"
  timezone: string;
  weekdays?: WeekdayCode[];
  one_off_date?: string | null;
  member_id?: string | null;
  enabled?: boolean;
}

export interface ReminderUpdateIn {
  message?: string;
  time_of_day?: string;
  timezone?: string;
  weekdays?: WeekdayCode[];
  one_off_date?: string | null;
  member_id?: string | null;
  enabled?: boolean;
}

// ─── Analytics (cycle 6) ────────────────────────────────────────────────

export interface TIRBuckets {
  urgent_low_pct: number;
  low_pct: number;
  in_range_pct: number;
  high_pct: number;
  urgent_high_pct: number;
}

export interface GlucoseSummary {
  start: string;
  end: string;
  reading_count: number;
  avg_sgv: number | null;
  std_sgv: number | null;
  cv_pct: number | null;
  min_sgv: number | null;
  max_sgv: number | null;
  gmi_pct: number | null;
  tir: TIRBuckets;
  target_low: number;
  target_high: number;
  urgent_low: number;
  urgent_high: number;
}

export interface DailySummary {
  date: string; // YYYY-MM-DD (UTC)
  reading_count: number;
  avg_sgv: number | null;
  std_sgv: number | null;
  cv_pct: number | null;
  min_sgv: number | null;
  max_sgv: number | null;
  gmi_pct: number | null;
  tir: TIRBuckets;
  target_low: number;
  target_high: number;
  urgent_low: number;
  urgent_high: number;
}

export interface DailySummaryResponse {
  count: number;
  days: DailySummary[];
}

// ─── Predictions stats (cycle 7) ────────────────────────────────────────

export interface PredictionLevelStats {
  count: number;
  confirmed: number;
  false_positive: number;
  superseded: number;
  hit_rate: number | null;
}

export interface LeadTimeStats {
  p50: number | null;
  p90: number | null;
  mean: number | null;
}

export interface PredictionStatsResponse {
  window_days: number;
  total_predictions: number;
  by_level: Record<PredictionLevel, PredictionLevelStats>;
  lead_time_seconds: LeadTimeStats;
  pending: number;
}

// ─── Settings PATCH body (cycle 4 + 7) ──────────────────────────────────
// Every field optional; cross-field validation runs server-side on the
// merged result so the client doesn't need to re-implement the rules.
export interface UserSettingsPatch {
  units?: "mg/dL" | "mmol/L";
  target_low?: number;
  target_high?: number;
  urgent_low?: number;
  urgent_high?: number;
  alerts_enabled?: boolean;
  // Cycle 228: repeating critical-alarm escalation. Cycle 422: the server
  // 422s anything outside 120–1800 s.
  alarm_repeat_enabled?: boolean;
  alarm_repeat_interval_seconds?: number;
  display_24h?: boolean;
  display_dark_mode?: boolean;
  // Cycle 118: IOB ring color overrides (null clears to the stock token
  // default). A set value must be a `#rrggbb` hex; the two colors can't be
  // equal — both validated server-side (422 on failure).
  iob_meal_color?: string | null;
  iob_correction_color?: string | null;
  // Cycle 303: IOB-card dismissal timestamp (UTC ISO-8601 or null).
  iob_card_dismissed_at?: string | null;
  // Cycle 147
  iob_basal_color?: string | null;
  // Cycle 84
  display_theme?: DisplayTheme;
  display_background_id?: string | null;
  // Cycle 171
  display_mobile_background?: MobileBackground;
  // Cycle 77
  nav_position?: "bottom" | "side";
  prediction_enabled?: boolean;
  prediction_horizon_minutes?: number;
  prediction_min_slope_abs?: number;
  prediction_min_r_squared?: number;
  // Cycle 16; cycle 330: an opaque voice_id.
  tts_voice?: string | null;
  tts_announce_interval_minutes?: number;
  // Cycle 330: voice quiet hours. Bad HH:MM 422s server-side.
  tts_quiet_hours_enabled?: boolean;
  tts_quiet_hours_start?: string;
  tts_quiet_hours_end?: string;
  // Cycle 145: full sub-object replace (the Settings card PATCHes the
  // whole schedule each time).
  alarm_clock?: AlarmClockSchedule;
  // Cycle 242: per-zone custom sound (unknown coerces to "default" server-side).
  alarm_sound_urgent_low?: string;
  alarm_sound_low?: string;
  alarm_sound_inrange?: string;
  alarm_sound_high?: string;
  alarm_sound_urgent_high?: string;
}

// ─── Members (cycle 12) ─────────────────────────────────────────────────

export interface Member {
  id: string;
  display_name: string;
  relationship_label: string | null;
  is_self: boolean;
  linked_user_id: string | null;
  // Cycle 60: present on members surfaced by /api/members + /api/me/members
  // (granted members included). Compare to the current user's id to
  // distinguish owned vs shared in the UI.
  owner_user_id: string | null;
  cgm_provider: string | null;
  created_at: string;
  archived_at: string | null;
  // Cycle 105: the member's avatar id (image at
  // /api/avatars/{avatar_id}/image), or null → the Sugalarm shield
  // fallback. Present on every member shape (list / dashboard / active).
  avatar_id: string | null;
}

export interface MemberWithLatest extends Member {
  latest_reading: Reading | null;
  stale: boolean;
  age_seconds: number | null;
  current_alert_level: AlertLevel | null;
  /** Cycle 468: the member's owner is locked out (monitoring suspended by
   *  the subscription lock); the row then carries no reading and no alert
   *  level. Optional: a pre-468 server omits it. */
  service_locked?: boolean;
}

export interface MemberListResponse {
  members: Member[];
  active_member_id: string | null;
}

export interface MemberDashboardResponse {
  members: MemberWithLatest[];
  active_member_id: string | null;
}

export interface ActiveMember {
  member_id: string;
  is_self: boolean;
  display_name: string;
  // Cycle 105: avatar id (null → shield). Additive.
  avatar_id?: string | null;
}

// ─── WebSocket frames (cycle 12: every typed frame carries member_id) ───

export interface SnapshotMember {
  member_id: string;
  reading: Reading | null;
  stale: boolean;
  age_seconds: number | null;
  /** Cycle 61: most recent alert row for this member (nullable —
   *  most members never have one). Used by useGlucoseSocket to seed
   *  `lastAlert`, which useAlarmEpisode then uses to resume the
   *  collapsed banner + snooze countdown on a device that connected
   *  mid-episode without waiting for the next Address / Silence
   *  press. Older clients tolerate the missing field (the snapshot
   *  reducer destructures it as optional). */
  latest_alert?: Alert | null;
  /** Cycle 410: the member's OPEN low-family rows (newest first). On a
   *  descent that is the urgent row AND its `low` sibling — the row the
   *  descent rule needs to keep the low loop sounding after a reconnect,
   *  wake or reload mid-lead-window. Audio selection only: the slot still
   *  comes from `latest_alert` through `preferSoundingRow`. Optional for
   *  wire-compat; absent = seed nothing extra. */
  open_alerts?: Alert[] | null;
  /** Cycle 88: true while this member has an unresolved definitive CGM
   *  credential rejection. Seeds `useGlucoseSocket`'s per-member auth
   *  state so a device connecting mid-failure shows the modal at once.
   *  Optional — older snapshots omit it. */
  cgm_auth_failed?: boolean;
  /** Cycle 468: the member's owner is locked out (monitoring suspended by
   *  the subscription lock). The row then carries no reading and no alert
   *  level; a follower's card shows the stopped line instead. */
  service_locked?: boolean;
}

export interface SnapshotFrame {
  type: "snapshot";
  members: SnapshotMember[];
  /** Cycle 362: un-acknowledged reminder occurrences, newest first. TOP-LEVEL
   *  because reminders are user-scoped, not member-scoped. This is the
   *  server-truth reconciliation path — a reconnecting / rebooting / freshly
   *  opened device converges on what is genuinely outstanding instead of
   *  trusting its own in-memory latch. Optional: absent from a pre-362
   *  server. */
  outstanding_reminders?: ReminderFire[];
}

export interface GlucoseFrame {
  type: "glucose";
  member_id: string;
  reading: Reading;
}

export interface AlertFrame {
  type: "alert";
  member_id: string;
  alert: Alert;
}

export interface PredictionFrame {
  type: "prediction";
  member_id: string;
  prediction: Prediction;
}

export interface StatusFrame {
  type: "status";
  member_id: string;
  message: string;
  [k: string]: unknown;
}

export interface PongFrame {
  type: "pong";
}

// ─── Cycle 24: bidirectional sync frames ────────────────────────────────

export interface TreatmentFrame {
  type: "treatment";
  member_id: string;
  treatment: Treatment;
}

export interface TreatmentDeletedFrame {
  type: "treatment_deleted";
  member_id: string;
  treatment_id: string;
}

export interface AlertAckFrame {
  type: "alert_ack";
  member_id: string;
  alert: Alert;
}

// ─── Cycle 38: cross-device alarm episode state ───────────────────────

export interface AlertSnoozedFrame {
  type: "alert_snoozed";
  member_id: string;
  alert: Alert;
}

export interface AlertSilencedFrame {
  type: "alert_silenced";
  member_id: string;
  alert: Alert;
}

/** Cycle 391: a treating window opened, was extended, or was torn down.
 *
 *  Mirrors the `alert_snoozed` shape exactly — the body is the FULL
 *  refreshed `AlertOut`, so a client updates its cache without a follow-up
 *  GET. Reaches the same cycle-60 member audience and rides the existing
 *  Postgres LISTEN/NOTIFY path across replicas.
 *
 *  A frame whose alert has `custody_until: null` is a TEARDOWN — every
 *  device re-arms together on it, which is what makes a worsening
 *  breakthrough feel instantaneous in the room. */
export interface AlertTreatingFrame {
  type: "alert_treating";
  member_id: string;
  alert: Alert;
}

/** Cycle 63: sustained-alarm heartbeat. Emitted by the alerts engine
 *  when a new reading arrives whose level is unchanged but still
 *  alarming — re-broadcasts the open alert row (refreshed
 *  `last_heartbeat_at`, current `snoozed_until` / `silenced_at`) so
 *  devices that connected mid-low or whose local episode flapped
 *  shut keep a bindable alert id. NOT a new alert, NOT an
 *  acknowledgement — the reducer must not bump `unackCount`. */
export interface AlertHeartbeatFrame {
  type: "alert_heartbeat";
  member_id: string;
  alert: Alert;
}

/** Cycle 82: server-authored recovery. Emitted by the alerts engine
 *  when the open episode's readings have sat back in-range for the
 *  recovery dwell — the body is the refreshed AlertOut carrying
 *  `recovered_at`. Every device clears the episode together (audio
 *  off, banner unmounts) instead of running independent local dwell
 *  timers. The reducer upserts the row like `alert_silenced`. */
export interface AlertRecoveredFrame {
  type: "alert_recovered";
  member_id: string;
  alert: Alert;
}

export interface PredictionAckFrame {
  type: "prediction_ack";
  member_id: string;
  prediction: Prediction;
}

export interface SettingsUpdateFrame {
  type: "settings_update";
  member_id: string;
  settings: UserSettings;
}

export interface MemberUpdateFrame {
  type: "member_update";
  member: Member;
}

export interface ActiveMemberChangedFrame {
  type: "active_member_changed";
  member_id: string;
}

// Cycle 76: a scheduled reminder fired. User-scoped (owner's sockets
// only) — no member_id discriminator, like active_member_changed.
export interface ReminderFiredFrame {
  type: "reminder_fired";
  reminder: Reminder;
  /** Cycle 362: the durable occurrence, so Close can acknowledge THIS fire.
   *  Null on a `/test` fire (which deliberately creates no obligation) and on
   *  a pre-362 server. */
  fire?: ReminderFire | null;
}

// Cycle 217: a reminder was created / edited / deleted on one of the
// owner's devices. User-scoped (owner's sockets only) signal frame — no
// body; each receiving device reloads its reminder list so an edit
// propagates across all devices instantly. The server commits the
// mutation before broadcasting, so the reload sees the committed change.
export interface RemindersChangedFrame {
  type: "reminders_changed";
}

// Cycle 457: a hero's picture changed (preset, photo, or photo removed).
// A signal with no body — every device holding this member re-fetches the
// roster / hero list.
export interface HeroRosterChangedFrame {
  type: "hero_roster_changed";
  member_id: string;
}

// Cycle 381: a voice call was just DIALED for this member, so the phone's
// in-app alarm cue should stay quiet briefly rather than play a second
// identical sound over the ringing call.
//
// `quiet_until` is an ABSOLUTE, SERVER-STAMPED instant, never a flag. A
// "call in progress" boolean would be the cycle-366 stuck-`in_progress`
// bug again (16 escalations stuck, one for 29 days) — a deadline expires
// on its own, so nothing has to remember to clear it. A client that does
// not understand this frame simply keeps sounding: silence always requires
// this frame to ARRIVE.
export interface VoiceCallPlacedFrame {
  type: "voice_call_placed";
  member_id: string;
  quiet_until: string;
}

// Cycle 403: we are on the phone with ONE specific account right now, so
// that account's mobile / web sessions cut their in-app alarm audio for
// the length of the call. The live human voice beats the chime.
//
// This is what the cycle-381 frame above could not be. That one is
// member-scoped and fires at DIAL time for everybody in the household,
// because contacts were phone numbers, push tokens were accounts, and
// nothing joined them. `emergency_contacts.linked_user_id` is that join,
// so this frame can name the one person we are actually talking to.
// Cycle 381's frame is UNCHANGED and still runs — it remains the fallback
// for an UNLINKED contact.
//
// ⚠ FOUR PROPERTIES, EACH LOAD-BEARING:
//   - `user_id` is the ONLY recipient this applies to. A session whose
//     user id differs ignores it completely.
//   - THE KIOSK IGNORES IT ENTIRELY, whatever the user id says. A kiosk
//     device session authenticates as the owner, so the user id WILL
//     match — the `isKiosk` guard, not the id, is what protects it.
//   - `until` is an ABSOLUTE, SERVER-STAMPED instant written from
//     `SUGALARM_CALL_QUIET_MAX_SECONDS`. The client never invents a
//     duration, so the client cap and the server ceiling are the same
//     number by construction.
//   - END OF QUIET IS A SECOND FRAME OF THIS SAME TYPE with `until`
//     already past, sent when the call reaches a terminal status. There
//     is deliberately no `call_quiet_end` type: one comparison
//     (`now < until`) drives everything, so a lost end-frame still ends
//     in LOUD when the ceiling passes.
//
// DISPLAY IS UNTOUCHED. This cuts sound and nothing else — no banner,
// hero or strip is removed from any screen by this frame.
export interface CallQuietFrame {
  type: "call_quiet";
  member_id: string;
  user_id: string;
  until: string;
}

/** Cycle 407: the check-in that asks before it gets loud. Broadcast to the
 *  member audience when a DECLARED treating window crosses
 *  `custody_until - 120s` (and again for the "do you need more time?"
 *  follow-up). It is the SPOKEN trigger — the row has already gone out on
 *  `alert_treating` with `custody_checkin_question` set, so a device that
 *  misses this frame still shows the question from its cached row.
 *
 *  Carries no glucose value, no name and no phone: every id is one the
 *  recipient already holds, and `expires_at` is server-written. */
export interface CustodyCheckinFrame {
  type: "custody_checkin";
  alert_id: string;
  member_id: string;
  question: CustodyCheckinQuestion;
  expires_at: string;
}

// Cycle 272: a FIRED reminder was snoozed / done on one of the owner's
// devices. User-scoped (owner's sockets only), so snooze/done propagate
// across every device the way the alarm snooze/silence frames do. No DB
// mutation — these are fired-instance actions, not config changes.
// `snooze_until_ms` is absolute epoch ms so all devices re-show together.
export interface ReminderSnoozedFrame {
  type: "reminder_snoozed";
  reminder_id: string;
  snooze_until_ms: number;
}

export interface ReminderDismissedFrame {
  type: "reminder_dismissed";
  reminder_id: string;
  /** Cycle 362: the acknowledged occurrence. Null from a pre-362 server or a
   *  test-fire dismiss; `reminder_id` is retained for that fallback. */
  fire_id?: string | null;
}

// Cycle 88: member-scoped CGM credential-failure state. `auth_failed`
// true on a definitive rejection; false when it clears (creds updated /
// poll succeeded). Drives the "update credentials" modal.
export interface CgmAuthFrame {
  type: "cgm_auth";
  member_id: string;
  auth_failed: boolean;
  provider: "dexcom" | "libre" | null;
  failed_at: string | null;
}

// Cycle 468: the member's monitoring is on or suspended by the
// subscription lock. Sent on connect (one per accessible member) and on
// every lock or unlock, so an open kiosk or app flips without polling and
// flips back on reactivation. Carries no other billing detail.
export interface ServiceStateFrame {
  type: "service_state";
  member_id: string;
  state: "active" | "locked";
  /** Cycle 468 (safety review round 3): when the server read the state it
   *  announces, so two frames delivered out of order converge on the newer
   *  one. Optional: a pre-468.1 frame has none and always applies. */
  as_of?: string | null;
}

// Cycle 97: operator broadcast banner. NOT member-scoped — no member_id
// (the one frame type for which that's expected). `cleared:true` removes
// the active broadcast with the matching `id` everywhere.
export type BroadcastLevel = "info" | "warn" | "urgent";

export interface AdminBroadcastFrame {
  type: "admin_broadcast";
  id: string;
  message?: string;
  level?: BroadcastLevel;
  expires_at?: string | null;
  cleared?: boolean;
}

export interface ActiveBroadcast {
  id: string;
  message: string;
  level: BroadcastLevel;
  expires_at: string | null;
}

// Cycle 192: a GLOBAL, ownership-free re-fetch nudge — the published-visible
// theme catalog changed. Carries NO per-user `owned` flag; each client
// re-fetches its own GET /api/theme-packs (which computes its owned flags
// server-side). Not member-scoped.
export interface ThemeCatalogUpdatedFrame {
  type: "theme_catalog_updated";
  at: string;
}

export type WSFrame =
  | SnapshotFrame
  | AdminBroadcastFrame
  | ThemeCatalogUpdatedFrame
  | GlucoseFrame
  | AlertFrame
  | PredictionFrame
  | StatusFrame
  | PongFrame
  | TreatmentFrame
  | TreatmentDeletedFrame
  | AlertAckFrame
  | AlertSnoozedFrame
  | AlertSilencedFrame
  | AlertTreatingFrame
  | AlertHeartbeatFrame
  | AlertRecoveredFrame
  | PredictionAckFrame
  | SettingsUpdateFrame
  | MemberUpdateFrame
  | ActiveMemberChangedFrame
  | ReminderFiredFrame
  | RemindersChangedFrame
  | HeroRosterChangedFrame
  | VoiceCallPlacedFrame
  | CallQuietFrame
  | CustodyCheckinFrame
  | ReminderSnoozedFrame
  | ReminderDismissedFrame
  | CgmAuthFrame
  | ServiceStateFrame;

// ─── Web Push (cycle 17) ────────────────────────────────────────────────

export interface PushSubscription {
  id: string;
  endpoint: string;
  user_agent: string | null;
  device_id: string | null;
  created_at: string;
  last_success_at: string | null;
  last_failure_at: string | null;
  failure_count: number;
  disabled_at: string | null;
}

export interface PushSubscriptionListResponse {
  subscriptions: PushSubscription[];
  vapid_public_key: string;
}

export interface PushVapidResponse {
  vapid_public_key: string;
}

export interface PushTestResponse {
  sent: number;
  failed: number;
}

// ─── Voice (cycle 16; reshaped cycle 330) ───────────────────────────────
//
// A voice is an opaque `voice_id` + a display name (the old
// `short_name`/`locale`/`gender` shape was specific to the previous
// engine's locale-partitioned catalog and is gone).
//
// There is deliberately NO engine/provider field here, and there must
// never be one — the household is never told which engine spoke.

export interface VoiceInfo {
  voice_id: string;
  name: string;
  friendly_name: string;
}

export interface VoiceListOut {
  voices: VoiceInfo[];
  default_voice: string;
}

// ─── Food-photo carb estimation (cycle 27) ──────────────────────────────
// Re-exported here so cross-cutting consumers (notes formatting, future
// calibration UI) can import the types without depending on api/photo.ts.

export type {
  PhotoConfidence,
  PhotoEstimate,
  PhotoItem,
} from "../api/photo";

// ─── Linked members (cycle 60) ──────────────────────────────────────────

export type MemberLinkInviteStatus =
  | "pending"
  | "accepted"
  | "revoked"
  | "expired";

export type MemberLinkRole = "owner" | "editor" | "viewer";

export interface MemberLinkInvite {
  id: string;
  member_id: string;
  invited_email: string;
  status: MemberLinkInviteStatus;
  expires_at: string;
  created_at: string;
  accepted_at: string | null;
  accepted_by_user_id: string | null;
}

/** Returned only on the create response — includes the 6-digit code
 *  the owner shares with the invited person. List endpoints omit it. */
export interface MemberLinkInviteWithCode extends MemberLinkInvite {
  code: string;
}

export interface MemberLinkInviteCreateIn {
  email: string;
}

export interface MemberLinkInviteListResponse {
  invites: MemberLinkInvite[];
}

export interface MemberLinkInviteAcceptResponse {
  member_id: string;
  owner_user_id: string;
  owner_display_name: string;
  role: MemberLinkRole;
}

export interface MemberLinkUnlinkResponse {
  member_id: string;
  unlinked_user_id: string;
}

export interface MemberLinkGrant {
  user_id: string;
  email: string;
  display_name: string;
  role: MemberLinkRole;
  created_at: string;
}

export interface MemberLinkGrantListResponse {
  links: MemberLinkGrant[];
}

// ─── Emergency contacts / SMS (cycle 59) ────────────────────────────────

export type EmergencyContactLevel =
  | "urgent_low"
  | "low"
  | "high"
  | "urgent_high";

export interface EmergencyContact {
  id: string;
  member_id: string;
  label: string;
  phone_e164: string;
  sms_enabled: boolean;
  notify_levels: EmergencyContactLevel[];
  // Cycle 128: voice-call escalation. `call_order` (lower = called
  // first) sequences the ordered call list; `voice_enabled` is the
  // per-contact opt-in to voice escalation on urgent low.
  call_order: number;
  voice_enabled: boolean;
  verified: boolean;
  // Cycle 89: confirmation sent, awaiting the recipient's inbound YES.
  awaiting_optin: boolean;
  // Cycle 403: the household account whose phone this is, or null.
  //
  // ⚠ IT IS A SUPPRESSION CAPABILITY, NOT A LABEL. While an escalation is
  // calling a linked contact, the server holds that account's repeating
  // critical push and broadcasts `call_quiet` so its mobile / web
  // sessions cut in-app alarm audio — the live human voice beats the
  // chime. The kiosk and every display are untouched. Editing it is
  // Settings-only; the kiosk renders the state READ-ONLY.
  //
  // `link_source` is `"manual"` or `"auto"`. Cycle 403 only ever writes
  // `"manual"`: automatic matching needs a verified phone number on the
  // user row, which this platform has never had.
  linked_user_id?: string | null;
  link_source?: string | null;
  /** Cycle 456: the preset picture key. */
  avatar_preset?: string | null;
  /** Cycle 457: the hero's own photo (authenticated path + `v`); wins over
   *  the preset. Absent on an older server. */
  avatar_photo?: HeroAvatarPhoto | null;
  /** Cycle 457: may THIS caller change the picture (the hero's own linked
   *  account or the household admin)? A display hint; the server decides. */
  can_change_picture?: boolean;
  created_at: string;
}

export interface EmergencyContactCreateIn {
  label: string;
  phone_e164: string;
  notify_levels?: EmergencyContactLevel[];
  sms_enabled?: boolean;
  // Cycle 128: optional voice opt-in (default false). `call_order` is
  // server-assigned (appended) on create.
  voice_enabled?: boolean;
  // Cycle 403: optional account link. The server validates the target is
  // a member of the caller's active household and stamps `link_source`
  // itself, so a client can never claim a link was inferred.
  linked_user_id?: string | null;
}

export interface EmergencyContactUpdateIn {
  label?: string;
  phone_e164?: string;
  notify_levels?: EmergencyContactLevel[];
  sms_enabled?: boolean;
  // Cycle 128: voice opt-in + per-contact order (PATCHable).
  voice_enabled?: boolean;
  call_order?: number;
  // Cycle 403: set or CLEAR the account link. Sending `null` UNLINKS;
  // omitting the key leaves an existing link alone.
  linked_user_id?: string | null;
}

export interface EmergencyContactListResponse {
  contacts: EmergencyContact[];
  sms_enabled: boolean;
  // Cycle 128: operator-configured voice-rail status (banner gate).
  voice_enabled: boolean;
  // Cycle 395: who last put a name to a treating window on THIS device,
  // already resolved server-side against the member in scope (so it is
  // always one of `contacts` above, or null). A pre-selection only; the
  // tile still has to be tapped, and nothing sends on its own.
  preselected_contact_id?: string | null;
}

// Cycle 128: reorder request + voice-test response.
export interface ReorderContactsIn {
  ordered_ids: string[];
}

export interface VoiceTestResponse {
  placed: boolean;
}

export interface VerifyStartResponse {
  sent: boolean;
  cooldown_seconds: number;
}

export interface SmsTestResponse {
  sent: boolean;
}

// ─── Result helper for fetch wrappers ───────────────────────────────────

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiError };

export type ApiErrorKind =
  | "network"
  | "auth"
  | "forbidden"
  | "not_found"
  | "validation"
  | "rate_limit"
  | "server"
  | "unknown";

export interface ApiError {
  kind: ApiErrorKind;
  status: number;
  message: string;
  details?: unknown;
}

/** Cycle 456: who took a treating window. "Someone else" carries null hero
 *  fields. No contact detail is ever on it. */
export type TreatingBySource =
  | "kiosk_pick"
  | "phone_pick"
  | "linked_account"
  | "keypress"
  | "someone_else";

export interface TreatingBy {
  hero_id: string | null;
  display_name: string | null;
  avatar_preset: string | null;
  source: TreatingBySource;
  at: string | null;
  /** Cycle 457, additive: the attributed hero's photo, when they have one. */
  avatar_photo?: HeroAvatarPhoto | null;
}

/** Cycle 457: a hero's own photo. `url` is an AUTHENTICATED app path (never
 *  a bucket URL), fetched with the bearer; `v` changes with every picture
 *  change, so a cache keyed on it never shows a stale photo. */
export interface HeroAvatarPhoto {
  url: string;
  v: string;
}

/** Cycle 456: one tile in the "Who's treating?" picker. */
export interface HeroRosterItem {
  id: string;
  display_name: string;
  avatar_preset: string;
  position: number;
  /** Cycle 457, additive: wins over the preset when present. */
  avatar_photo?: HeroAvatarPhoto | null;
}
